import { priceDateKey } from './priceDateKey';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { computeBenefitPrices } from './computeBenefitPrices';
import { roundUpPrice } from './formatPrice';

export const computeCircuitDayMinimumPrice = ({
  circuitDay,
  date,
  nbParticipants,
  nbChildren = 0,
  nbAdolescents = 0,
  circuitMarginRate = 0,
  shouldIgnoreNoPrice = false,
  indexDay,
  targetCurrency,
  convertPrice,
}: {
  circuitDay: CircuitFormData['circuitDays'][number];
  date: Date;
  nbParticipants: number;
  nbChildren?: number;
  nbAdolescents?: number;
  circuitMarginRate?: number;
  shouldIgnoreNoPrice?: boolean;
  indexDay: number;
  targetCurrency: string;
  convertPrice: (
    price: number,
    fromCurrency: string,
    toCurrency: string,
  ) => number;
}) => {
  const circuitBenefits = circuitDay.steps
    .flatMap((step) => [
      step.transport,
      step.activities,
      step.accommodations,
      step.meals,
    ])
    .filter((step) => step.standardBenefit)
    .flatMap((step) => ({
      benefit: step.standardBenefit,
      nbDays: step.nbDays,
    }));
  const benefitPrices = circuitBenefits.map(({ benefit, nbDays }) => {
    const parsedBenefit = benefit as unknown as BenefitFormData;
    const currency = parsedBenefit.currency;
    const price = computeBenefitPriceForDate({
      benefit: parsedBenefit,
      day: date,
      nbParticipants,
      circuitMarginRate,
      nbDays,
      nbChildren,
      nbAdolescents,
      indexDay,
    });
    return convertPrice(price, currency, targetCurrency);
  });

  if (benefitPrices.includes(0) && !shouldIgnoreNoPrice) return -1;

  return roundUpPrice(benefitPrices.reduce((a, b) => a + b, 0));
};

const computeBenefitPriceForDate = ({
  benefit,
  day,
  nbParticipants,
  circuitMarginRate,
  nbChildren = 0,
  nbAdolescents = 0,
  nbDays = 1,
  indexDay,
}: {
  benefit: BenefitFormData;
  day: Date;
  nbParticipants: number;
  circuitMarginRate?: number;
  nbDays?: number;
  nbChildren?: number;
  nbAdolescents?: number;
  indexDay: number;
}) => {
  const { marginRate, prices } = computeBenefitPrices({
    benefit,
    nbParticipants,
    circuitMarginRate,
    nbDays,
    nbChildren,
    nbAdolescents,
    indexDay,
  });

  const pricesForDay = prices.get(priceDateKey(day));

  return pricesForDay
    ? roundUpPrice(pricesForDay.minimumPrice * (1 + marginRate / 100))
    : 0;
};
